import React, { useEffect, useRef, useState } from 'react';

import { useLanguages } from '../../hooks/useLanguages';
import { postSessionLanguage } from '../../api';
import { writeLanguageCookie } from '../../util/cookies';
import { getLanguageEvents } from '../../util/languageEvents';

import css from './LanguageSelector.module.css';

/**
 * Capability-gated language selector (the ShareGardens LanguageSelector port,
 * made self-contained — no host Menu components, no host Redux ducks):
 *
 *   - gates on the tenant's `multilanguage` capability block (enabled +
 *     languages[]); renders NOTHING until capabilities resolve — which also
 *     makes the codemod-wired mount hydration-safe (server HTML has no
 *     selector; the first client render matches it)
 *   - language names are browser-native endonyms via Intl.DisplayNames
 *     (de → "Deutsch"); no hardcoded language map
 *   - on switch: emits 'languageChanged', POSTs /session-language (sets the
 *     emp_lang cookie the SSR resolver reads; cookie written client-side as a
 *     fallback when the call fails), optionally persists
 *     `protectedData.preferredLanguage` (D5) through the host-provided
 *     `onPersistPreference`, then reloads — SSR re-renders translated
 *   - two modes (TASK175): `floating` (fixed bottom-left chip, zero layout
 *     risk — the codemod default) and `inline` (for a Topbar slot)
 *   - themeable via CSS vars: --emp-lang-bg, --emp-lang-color,
 *     --emp-lang-border, --emp-lang-radius, --emp-lang-z, --emp-lang-active-bg
 *
 * @param {object} props
 * @param {'floating'|'inline'} [props.mode='inline']
 * @param {string} [props.rootUrl]
 * @param {(update: { protectedData: { preferredLanguage: string } }) => Promise<any>} [props.onPersistPreference]
 *        Host hook for the D5 profile write (e.g. dispatch(updateProfile(...))
 *        for logged-in users). Optional — cookie persistence always applies.
 * @param {string} [props.className]
 */
const languageDisplayName = code => {
  try {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code);
    if (name && name !== code) return name.charAt(0).toUpperCase() + name.slice(1);
  } catch (_) {
    // very old browsers — fall through
  }
  return code.toUpperCase();
};

export const LanguageSelector = props => {
  const { mode = 'inline', rootUrl, onPersistPreference, className } = props;
  const { ready, enabled, languages, current } = useLanguages({ rootUrl });
  const [isOpen, setOpen] = useState(false);
  const [isSwitching, setSwitching] = useState(false);
  const rootRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocClick = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [isOpen]);

  if (!ready || !enabled) {
    return null;
  }

  const handleSwitch = async code => {
    if (code === current || isSwitching) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    setOpen(false);

    getLanguageEvents().emit('languageChanged', code);

    // Server-side cookie first (what SSR reads); client-side fallback if the
    // call fails so at least this browser keeps the choice.
    const result = await postSessionLanguage(code, { rootUrl });
    if (!result || !result.ok) {
      writeLanguageCookie(code);
    }

    // D5: persist protectedData.preferredLanguage for logged-in users when the
    // host wires its profile-update action in. Await BEFORE reloading (the SG
    // lesson) — but never let it block the switch.
    if (typeof onPersistPreference === 'function') {
      try {
        await onPersistPreference({ protectedData: { preferredLanguage: code } });
      } catch (_) {
        // profile write is best-effort; the cookie already holds the choice
      }
    }

    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  const rootClass = [css.root, mode === 'floating' ? css.floating : css.inline, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} ref={rootRef}>
      <button
        type="button"
        className={css.trigger}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Change language"
        onClick={() => setOpen(open => !open)}
        disabled={isSwitching}
      >
        <span className={css.globe} aria-hidden="true">
          🌐
        </span>
        <span className={css.currentCode}>{current.toUpperCase()}</span>
      </button>
      {isOpen ? (
        <ul className={css.menu} role="listbox" aria-label="Languages">
          {languages.map(code => (
            <li key={code} role="option" aria-selected={code === current}>
              <button
                type="button"
                className={code === current ? `${css.option} ${css.optionActive}` : css.option}
                onClick={() => handleSwitch(code)}
              >
                <span className={css.optionCode}>{code.toUpperCase()}</span>
                <span className={css.optionName}>{languageDisplayName(code)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default LanguageSelector;
